import { JSX } from 'react';
import { KibButtonNew } from '@chewy/kib-controls-react';

import { StethoscopeIcon } from '@src/components/icons/stethoscope';
import { BandageIcon } from '@src/components/icons/bandage';
import { LegalText } from '@src/components/legal';

import Intro from '@src/pages/landing/components/sections/intro';
import Note from '@src/pages/landing/components/sections/note';
import Plans from '@src/pages/landing/components/sections/plans';
import Meet from '@src/pages/landing/components/meet';
import Diff from '@src/pages/landing/components/diff';
import Info from '@src/pages/landing/components/info';
import Partners from '@src/pages/landing/components/partners';
import Teams from '@src/pages/landing/components/teams';
import Ask from '@src/pages/landing/components/ask';
import Faqs from '@src/pages/landing/components/faqs';
import { chewyCarePlusSupport } from '@src/constants/phone-numbers';

export function StaticLandingPage({
  onSectionView,
  onLinkClicked,
  onFetchPlansClicked,
}: {
  onSectionView: (
    label: string,
    value: string,
    extraData?: Record<string, string>,
  ) => void;
  onLinkClicked: (
    label: string,
    value: string,
    extraData: Record<string, string>,
  ) => void;
  onFetchPlansClicked: (label: string, value: string) => void;
}): JSX.Element {
  return (
    <main className="hc-pet-insurance__container" aria-label="Content">
      <Intro
        onLinkClicked={onLinkClicked}
        onFetchPlansClicked={onFetchPlansClicked}
        onSectionView={onSectionView}
      />

      <Meet
        analytics={{
          label: 'meetCarePlus<small>*</small>',
          value: 'Meet CarePlus<small>*</small>',
        }}
        onSectionView={onSectionView}
        descendants={[
          {
            icon: <StethoscopeIcon aria-label="Routine care" />,
            title: 'Wellness Coverage',
            text: (
              <>
                Coverage that <strong>keeps them healthy</strong>, including
                preventative care like routine veterinarian visits,
                vaccinations, and flea, tick, and heartworm medications.
              </>
            ),
          },
          {
            icon: <BandageIcon aria-label="Unexpected treatment" />,
            title: 'Accidents & Illnesses',
            text: (
              <>
                Coverage for <strong>when they&apos;re hurt or sick</strong>,
                including big or small injuries, orthopedic issues, treatments
                for skin conditions, digestive issues, chronic conditions, and
                age-related diseases.
              </>
            ),
          },
        ]}
      />

      <Diff
        analytics={{
          label: 'theCarePlusDifference',
          value: 'The CarePlus Difference',
        }}
        onSectionView={onSectionView}
        descendants={[
          {
            title: <span>Direct Payments</span>,
            text: 'Your invoice can be paid to the veterinarian directly within minutes, or you can submit a claim in 3 easy steps.',
            image: '/pet-insurance/img/transaction.png',
          },
          {
            title: <span>100% Coverage for Rx & More</span>,
            text: 'Chewy customers can get 100% of costs covered (less deductibles) for veterinary diet food, prescription medications, and supplements included in their plan.',
            image: '/pet-insurance/img/rx.png',
          },
          {
            title: <span>No Payout Limits</span>,
            text: 'Your pet can receive unlimited lifetime insurance coverage without caps.',
            image: '/pet-insurance/img/wallet.png',
          },
          {
            title: <span>Live Vet Access</span>,
            text: "Access to Chewy's popular Connect with a Vet telehealth service. FREE and unlimited with most plans.",
            image: '/pet-insurance/img/phone.png',
          },
        ]}
      />

      <Plans
        onFetchPlansClicked={onFetchPlansClicked}
        onLinkClicked={onLinkClicked}
        onSectionView={onSectionView}
      />

      <Info
        analytics={{
          label: 'insurancedevelopedbypetloversforpetlovers.',
          value: 'Insurance developed by pet lovers for pet lovers.',
        }}
        onSectionView={onSectionView}
        descendants={[
          {
            title: '24/7 Customer Care',
            text: "Enjoy access to the same award-winning customer care you're used to getting with Chewy.",
          },
          {
            title: 'Curated By a Name You Know',
            text: 'No matter which CarePlus wellness or insurance plan you choose, feel confident that the team at Chewy worked tirelessly with pet-obsessed employees and veterinarians to develop simple plans that put your pets first.',
          },
          {
            title: 'Easy, Instant Access to Vets',
            text: "Free, unlimited access to licensed veterinarians with most plans, available 7 days a week via chat or video through Chewy's telehealth service, Connect with a Vet. Our veterinarians are ready to answer all your pet health questions.",
          },
        ]}
      />

      <Note
        onFetchPlansClicked={onFetchPlansClicked}
        onLinkClicked={onLinkClicked}
        onSectionView={onSectionView}
      />

      <Partners
        analytics={{
          label: 'ourtrustedpartners',
          value: 'Our Trusted Partners',
        }}
        onSectionView={onSectionView}
      />

      <Teams
        analytics={{
          label: 'trupanionlemonade',
          value: 'Trupanion Lemonade',
        }}
        onSectionView={onSectionView}
        descendants={[
          [
            {
              title: <>24/7 live agents</>,
            },
            {
              title: (
                <>
                  Direct-to-Vet claims <span>payments</span>
                </>
              ),
            },
            {
              title: (
                <>
                  20+ years <span>insurance experience</span>
                </>
              ),
            },
          ],
          [
            {
              title: (
                <>
                  Fast app-based <span>service</span>
                </>
              ),
            },
            {
              title: (
                <>
                  Instant claims <span>payment</span>
                </>
              ),
            },
            {
              title: (
                <>
                  Support charities <span>you care about</span>
                </>
              ),
            },
          ],
        ]}
      />

      <Faqs onSectionView={onSectionView} />

      <Ask
        link={{
          text: 'Contact Us',
          url: chewyCarePlusSupport.link,
          analytics: {
            label: 'contactUs',
            value: 'Contact Us',
          },
        }}
        onSectionView={onSectionView}
        onLinkClicked={onLinkClicked}
        analytics={{
          label: 'contactUs',
          value: 'Contact Us',
        }}
      />

      <div className="hc-pet-insurance__legal">
        <LegalText />
      </div>

      <div className="pi-careplus__sticky">
        <KibButtonNew
          onClick={() => {
            onFetchPlansClicked('stickyFetchPlans', 'Fetch Plans');
          }}
          size="large"
        >
          Fetch Plans
        </KibButtonNew>
      </div>
    </main>
  );
}
